import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { GenericCRUDService } from '@/services/genericCRUD';
import { tenantEntity } from '@/config/entities/tenants';
import { toast } from 'react-hot-toast';
import { 
  PencilIcon, 
  TrashIcon, 
  ArrowLeftIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  UserGroupIcon,
  CalendarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

export const TenantDetailPage: React.FC = () => {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  
  const crudService = new GenericCRUDService(tenantEntity);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const data = await crudService.getById(id!);
        setTenant(data);
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || 'Failed to load tenant details.';
        toast.error(errorMessage);
        navigate('/tenants');
      } finally {
        setIsLoading(false);
      }
    };

    if (id) {
      fetchTenant();
    }
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return;
    }

    setIsDeleting(true);
    try {
      await crudService.delete(id!);
      toast.success('Tenant deleted successfully!');
      navigate('/tenants');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to delete tenant.';
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-[var(--color-success)]/10 text-[var(--color-success)]';
      case 'inactive':
        return 'bg-[var(--color-border)] text-[var(--color-secondary-text)]';
      case 'suspended':
        return 'bg-[var(--color-error)]/10 text-[var(--color-error)]';
      default:
        return 'bg-[var(--color-border)] text-[var(--color-secondary-text)]';
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] p-6 rounded-lg">
          <h1 className="text-3xl font-bold text-[var(--color-secondary-text)]">Loading...</h1>
        </div>
        <div className="bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] p-6 rounded-lg">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-[var(--color-secondary-text)] bg-opacity-20 rounded w-1/4"></div>
            <div className="h-4 bg-[var(--color-secondary-text)] bg-opacity-20 rounded w-1/2"></div>
            <div className="h-4 bg-[var(--color-secondary-text)] bg-opacity-20 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="space-y-6">
        <div className="bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] p-6 rounded-lg">
          <h1 className="text-3xl font-bold text-[var(--color-secondary-text)]">{t('tenant.tenantNotFound')}</h1>
        </div>
        <div className="bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] p-6 rounded-lg">
          <p className="text-[var(--color-secondary-text)]">{t('tenant.tenantNotFoundDescription')}</p>
          <Link to="/tenants" className="text-[var(--color-secondary-text)] hover:text-[var(--color-accent-text)] underline">
            {t('tenant.backToTenants')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] p-6 rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link 
              to="/tenants" 
              className="p-2 text-[var(--color-secondary-text)] hover:text-[var(--color-accent-text)] transition-colors"
            >
              <ArrowLeftIcon className="h-6 w-6" />
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-secondary-text)]">{tenant.name}</h1>
              <p className="mt-2 text-[var(--color-secondary-text)]">{t('tenant.tenantDetails')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to={`/tenants/${id}/edit`}
              className="inline-flex items-center px-4 py-2 border border-[var(--color-border)] text-sm font-medium rounded-md text-[var(--color-secondary-text)] bg-transparent hover:bg-[var(--color-accent-bg)] hover:text-[var(--color-accent-text)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-accent-bg)] transition-colors"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              {t('tenant.edit')}
            </Link>
            <button
              onClick={handleDelete}
              disabled={isDeleting}
              className="inline-flex items-center px-4 py-2 border border-[var(--color-border)] text-sm font-medium rounded-md text-[var(--color-secondary-text)] bg-transparent hover:bg-[var(--color-error)] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--color-error)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              {isDeleting ? t('tenant.deleting') : t('tenant.delete')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-transparent border border-[var(--color-border)] text-[var(--color-secondary-text)] p-6 rounded-lg">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-[var(--color-secondary-text)] flex items-center">
              <BuildingOfficeIcon className="h-5 w-5 mr-2" />
              {t('tenant.companyInformation')}
            </h2>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)]">{t('tenant.companyName')}</label>
                <p className="mt-1 text-[var(--color-secondary-text)]">{tenant.name}</p>
              </div>
              
              {tenant.domain && (
                <div>
                  <label className="block text-sm font-medium text-[var(--color-secondary-text)] flex items-center">
                    <GlobeAltIcon className="h-4 w-4 mr-1" />
                    {t('tenant.domain')}
                  </label>
                  <p className="mt-1 text-[var(--color-secondary-text)]">{tenant.domain}</p>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)]">{t('tenant.status')}</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${getStatusColor(tenant.status)}`}>
                  {tenant.status}
                </span>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-[var(--color-secondary-text)] flex items-center">
              <UserGroupIcon className="h-5 w-5 mr-2" />
              {t('tenant.statistics')}
            </h2>
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)]">{t('tenant.totalUsers')}</label>
                <p className="mt-1 text-2xl font-bold text-[var(--color-secondary-text)]">{tenant.user_count || 0}</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-1" />
                  {t('tenant.created')}
                </label>
                <p className="mt-1 text-[var(--color-secondary-text)]">
                  {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[var(--color-secondary-text)] flex items-center">
                  <ClockIcon className="h-4 w-4 mr-1" />
                  Last Updated
                </label>
                <p className="mt-1 text-[var(--color-secondary-text)]">
                  {tenant.updated_at ? new Date(tenant.updated_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 


